import {
  type CartItem as PodCartItem,
  type CartItemCreation,
  type CartItemUpdate,
  type CartListParams,
  podCartApi,
} from '@mathflat/domain/PodCart'
import {
  type Coupon,
  type CouponListParams,
  type CouponRegistration,
  type DirectOrder,
  type KmmOrderCreation,
  type KmmPaymentConfirmation,
  type OrderItem,
  type OrderListParams,
  type OrderStatus,
  type ParentOrder,
  type ParentOrderCreation,
  type ParentOrderOfflinePayment,
  type ParentOrderResend,
  type ParentPaymentConfirmation,
  type ParentPaymentParams,
  podOrderApi,
  type PodUserDeliveryAddressUpdate,
  type PreOrder,
  type PreOrderCouponListParams,
  type PreOrderCouponUpdate,
  type PreOrderCreation,
  type PreOrderDeliveryAddressUpdate,
  type PreOrderPaymentConfirmation,
  type PreOrderPointUpdate,
} from '@mathflat/domain/PodOrder'
import {
  type KmmProduct,
  type KmmProductListParams,
  podProductApi,
  type PrintDocumentExpectedAmountParams,
  type PrintDocumentProduct,
  type SignatureWorkbookProduct,
  type SignatureWorkbookProductListParams,
} from '@mathflat/domain/PodProduct'
import {
  type MonthlySettlementParams,
  podSettlementApi,
  type SettlementAccountRegistration,
} from '@mathflat/domain/PodSettlement'

import CheckableMap from '@/models/CheckableMap'
import { CartItem } from '@/page/PODMall/pages/PODMallCart/_dto/CartItem'
import { CartItemColl } from '@/page/PODMall/pages/PODMallCart/_dto/CartItemColl'
import commonRepo from '@/repository/common.repo'
import { mixpanelClient } from '@/utils/trackings/mixpanel'
import { couponBenefitMap } from '@/utils/trackings/schema/userProperties.schema'

// podAxios 인터셉터가 AxiosError 를 CommonError 로 바꿔 reject 하므로 `instanceof AxiosError` 로는
// 상태코드를 못 읽는다. 두 형태 모두에서 상태코드를 꺼낸다.
const resolveHttpStatus = (error: unknown) => {
  const err = error as { status?: number; response?: { status?: number } } | null | undefined
  return err?.status ?? err?.response?.status
}

// 교재몰 서버는 "아직 없음"(할인 이벤트·자동 주문 미신청)을 404 로 답한다. 화면은 이를 null 로 다룬다.
const nullIfNotFound = async <T>(request: Promise<T>): Promise<T | null> => {
  try {
    return await request
  } catch (e) {
    if (resolveHttpStatus(e) === 404) {
      return null
    }
    throw e
  }
}

class PODMallController {
  // -- Product
  async fetchSignatureWorkbookProductList(params: SignatureWorkbookProductListParams) {
    const { content, last } = await podProductApi.getSignatureWorkbookProducts(params)
    return { data: content, last }
  }

  async fetchKMMProductList(params: KmmProductListParams) {
    const { content, last } = await podProductApi.getKmmProducts(params)
    return {
      data: [...content].sort((a, b) => a.orderingNumber - b.orderingNumber),
      last,
    }
  }

  async fetchSignatureWorkbookProductDetail(id: SignatureWorkbookProduct['id']) {
    return await podProductApi.getSignatureWorkbookProductDetail(id)
  }

  fetchPrintDocumentProduct = async (): Promise<PrintDocumentProduct | null> => {
    const { content } = await podProductApi.getPrintDocumentProducts({ size: 1 })
    return content[0] ?? null
  }

  fetchExpectedPrice = async (
    productId: PrintDocumentProduct['id'],
    params: PrintDocumentExpectedAmountParams,
  ) => {
    return await podProductApi.getPrintDocumentExpectedAmount(productId, params)
  }

  // -- User
  async getUser() {
    return await podOrderApi.getPodUser()
  }

  async postUserDeliveryAddress(payload: PodUserDeliveryAddressUpdate) {
    return await podOrderApi.updatePodUserDeliveryAddress(payload)
  }

  // -- CartItem
  async getCartItems(params?: CartListParams) {
    const { cartItems, deliveryAmount } = await podCartApi.getCart(params)

    return {
      deliveryAmount,
      cartItemColl: new CartItemColl(
        CheckableMap.fromArr(
          cartItems.map((item) => CartItem.fromCartItem(item)),
          'id',
        ),
      ),
    }
  }

  async putCartItem(cartItemId: PodCartItem['id'], params: CartItemUpdate) {
    return await podCartApi.updateCartItem(cartItemId, params)
  }

  async postCartItem(payload: CartItemCreation) {
    return await podCartApi.createCartItem(payload)
  }

  async deleteCartItem(cartItemIds: PodCartItem['id'][]) {
    return await podCartApi.deleteCartItems(cartItemIds)
  }

  async getCartItemPreview(cartItemId: PodCartItem['id']) {
    return await podCartApi.getCartItemPdfDownload(cartItemId)
  }

  // -- PreOrder
  async getPreOrder({ id }: Pick<PreOrder, 'id'>) {
    return await podOrderApi.getPreOrder(id)
  }

  async postPreOrder(payload: PreOrderCreation) {
    return await podOrderApi.createPreOrder(payload)
  }

  async putPreOrder(payload: PreOrderDeliveryAddressUpdate) {
    return await podOrderApi.updatePreOrderDeliveryAddress(payload)
  }

  async getDeliveryLeadTime() {
    return await podOrderApi.getDeliveryLeadTime()
  }

  /* Order Start */
  async getOrders(params: OrderListParams) {
    return await podOrderApi.getDirectOrders(params)
  }

  async getOrderItems(id: DirectOrder['id']) {
    return await podOrderApi.getOrderItems(id)
  }

  async cancelOrder(id: DirectOrder['id']) {
    return await podOrderApi.cancelOrder(id)
  }

  async getOrderItemPreview(orderItemId: OrderItem['id']) {
    return await podOrderApi.getOrderItemPdfDownload(orderItemId)
  }

  async getPrintOption() {
    const { pixelMm } = await podOrderApi.getPrintOption()
    return { pixelMm: Number(pixelMm) }
  }

  async getParentOrders(params: OrderListParams) {
    return await podOrderApi.getParentOrders(params)
  }

  async getParentOrderDetail(groupOrderId: ParentOrder['groupOrderId']) {
    return await podOrderApi.getParentOrderDetail(groupOrderId)
  }
  /* Order End */

  /* Payment Start */
  async postPayment(payload: PreOrderPaymentConfirmation) {
    return await podOrderApi.confirmPreOrderPayment(payload)
  }

  async postParentPaymentToss(payload: ParentPaymentConfirmation) {
    return await podOrderApi.confirmParentPayment(payload)
  }

  async getParentPayment(params: ParentPaymentParams) {
    return await podOrderApi.getParentPayment(params)
  }

  async postParentPaymentSend_v2(payload: ParentOrderCreation) {
    return await podOrderApi.createParentOrders(payload)
  }

  async resendParentPayment(payload: ParentOrderResend) {
    return await podOrderApi.resendParentOrder(payload)
  }

  async validatePaymentToken(params: ParentPaymentParams) {
    return await podOrderApi.checkParentPaymentToken(params)
  }

  async payParentPaymentToDirect(payload: ParentOrderOfflinePayment) {
    return await podOrderApi.payParentOrderOffline(payload)
  }
  /* Payment End */

  // -- Coupon
  async getCouponByPreOrderId(preOrderId: PreOrder['id'], params?: PreOrderCouponListParams) {
    return await podOrderApi.getPreOrderCoupons(preOrderId, params)
  }

  async getCoupon(params?: CouponListParams) {
    const coupons = await podOrderApi.getCoupons(params)
    if (commonRepo.academy?.academyId) {
      mixpanelClient.setGlobalEventProperty({
        academy_프린팅쿠폰목록: coupons.map((coupon) => ({
          쿠폰이름: coupon.name,
          쿠폰ID: coupon.id,
          쿠폰혜택: couponBenefitMap[coupon.benefitType],
          쿠폰혜택값: coupon.benefitValue,
          쿠폰만료일: new Date(coupon.endDate),
          쿠폰활성상태: coupon.usable,
        })),
      })
    }

    return coupons
  }

  async putUserCoupon(payload: PreOrderCouponUpdate) {
    return await podOrderApi.updatePreOrderCoupon(payload)
  }

  async postUserCouponByCode(userCouponId: Coupon['id'], payload: CouponRegistration) {
    return await podOrderApi.registerCoupon(userCouponId, payload)
  }

  /* 요금제별 할인 정보 - user */
  async getProductDiscountEvent() {
    return await nullIfNotFound(podOrderApi.getProductDiscountEvent())
  }

  /* 요금제별 할인 정보 - preOrder */
  async getProductDiscountEventByPreOrder(preOrderId: PreOrder['id']) {
    return await nullIfNotFound(podOrderApi.getPreOrderProductDiscountEvent(preOrderId))
  }

  /* 요금제별 할인 적용 */
  async putProductDiscountEvent(preOrderId: PreOrder['id'], userCouponId: Coupon['id'] | null) {
    return await podOrderApi.updatePreOrderProductDiscountEvent(preOrderId, {
      action: userCouponId === null ? 'DISABLE_EVENT' : 'ENABLE_EVENT',
    })
  }

  putUserPoint = async (preOrderId: PreOrder['id'], payload: PreOrderPointUpdate) => {
    return await podOrderApi.updatePreOrderPoint(preOrderId, payload)
  }

  /* 정산 관련 Start */
  async getSettlementListByYearMonth(yearMonth: MonthlySettlementParams['targetYearMonth']) {
    return await podSettlementApi.getMonthlySettlement({ targetYearMonth: yearMonth })
  }

  async registerSettlementInfo(params: SettlementAccountRegistration, skipErrorToast?: boolean) {
    return podSettlementApi.registerSettlementAccount(params, {
      preventErrorToast: skipErrorToast ? true : undefined,
    })
  }
  /* 정산 관련 End */

  /**
   * KMM 상장&배너 주문 관련 Start
   */
  async createOrderV1(payload: KmmOrderCreation) {
    return await podOrderApi.createKmmOrder(payload)
  }
  async confirmPayment(payload: KmmPaymentConfirmation) {
    return await podOrderApi.confirmKmmPayment(payload)
  }
  async getKMMOrder(productId: KmmProduct['id'], statuses?: OrderStatus[]) {
    return await podOrderApi.getKmmOrders(productId, statuses ? { statuses } : undefined)
  }
  /* KMM 상장&배너 주문 관련 END */
}

export const podMallController = new PODMallController()
